/** The member's side panel, one for the whole account area. */

import { Suspense, useEffect } from 'react'
import { Outlet, useLocation } from 'react-router-dom'
import { useQuery } from '@tanstack/react-query'
import { ROUTES } from '@metal/shared'
import { Rail, Skeleton, type RailGroup, type RailRow } from '@metal/ui'
import { api } from '@metal/ui/api'
import { useSession } from '@metal/ui/session'
import { qk } from '@/lib/queries'
import { useSettingsSummary } from '@/screens/account/SettingsRail'

interface RailCounts {
  orders: number | null
  bids: number | null
  deals: number | null
  notifications: number | null
  documents: number | null
  transactions: number | null
  favorites: number | null
  requests: number | null
}

interface DashboardHead {
  side: 'seller' | 'buyer'
  counts?: RailCounts
}

/** Which row the current path belongs to. */
function activeOf(path: string): string | undefined {
  const rules: Array<[RegExp, string]> = [
    [/^\/dashboard/, 'overview'],
    [/^\/offers\/mine/, 'offers'],
    [/^\/rfqs\/mine/, 'rfqs'],
    [/^\/bids/, 'bids'],
    [/^\/deals/, 'deals'],
    [/^\/notifications/, 'notifications'],
    [/^\/documents/, 'documents'],
    [/^\/wallet/, 'transactions'],
    [/^\/favorites/, 'favorites'],
    [/^\/(requests|claims)/, 'requests'],
    [/^\/account\/grade/, 'grade'],
    [/^\/settings\/profile/, 'settings-profile'],
    [/^\/settings\/users/, 'settings-users'],
    [/^\/settings\/kyc/, 'settings-kyc'],
    [/^\/settings\/notifications/, 'settings-notifications'],
    [/^\/settings\/security/, 'settings-security'],
  ]
  return rules.find(([re]) => re.test(path))?.[1]
}

export function AccountRail() {
  const { pathname } = useLocation()
  const { user } = useSession()
  const trading = user?.membership === 'active'
  const sells = Boolean(user?.sides.includes('sell'))
  const buys = Boolean(user?.sides.includes('buy'))

  const head = useQuery({
    queryKey: [...qk.dashboard, 'rail'],
    queryFn: () => api.get<DashboardHead>('/dashboard'),
    enabled: trading,
    staleTime: 60_000,
  })
  const settings = useSettingsSummary()
  const c = head.data?.counts
  const countedSide = head.data?.side

  // A file not trading yet opens only the overview, its notifications and its settings.
  const locked = !trading
  const row = (r: RailRow): RailRow => (locked ? { ...r, disabled: true } : r)

  const orderRows: RailRow[] = []
  if (sells || (!sells && !buys)) {
    orderRows.push(
      row({
        key: 'offers',
        to: ROUTES.myOffers(),
        label: 'عرضه‌ها',
        icon: 'cathode',
        count: countedSide === 'seller' ? c?.orders ?? undefined : undefined,
      }),
    )
  }
  if (buys) {
    orderRows.push(
      row({
        key: 'rfqs',
        to: ROUTES.myRfqs(),
        label: 'تقاضاها',
        icon: 'list',
        count: countedSide === 'buyer' ? c?.orders ?? undefined : undefined,
      }),
    )
  }

  // On a phone the rail is one scrolling strip:
  useEffect(() => {
    const strip = document.querySelector<HTMLElement>('.account-rail')
    const on = strip?.querySelector<HTMLElement>('a.is-on')
    if (!strip || !on || strip.scrollWidth <= strip.clientWidth) return
    strip.scrollLeft = on.offsetLeft - (strip.clientWidth - on.offsetWidth) / 2
  }, [pathname])

  const inSettings = pathname.startsWith('/settings')
  const profileLabel = user?.partyKind === 'person' ? 'نمایه کاربر' : 'نمایه شرکت'

  const groups: RailGroup[] = [
    { rows: [{ key: 'overview', to: ROUTES.dashboard(), label: 'نمای کلی', icon: 'grid' }] },
    {
      heading: 'بخش‌های داشبورد',
      rows: [
        ...orderRows,
        row({ key: 'bids', to: ROUTES.bids(), label: 'پیشنهادها', icon: 'gavel', count: c?.bids ?? undefined }),
        row({ key: 'deals', to: ROUTES.deals(), label: 'معاملات', icon: 'exchange', count: c?.deals ?? undefined }),
        {
          key: 'notifications',
          to: ROUTES.notifications(),
          label: 'اعلان‌ها',
          icon: 'bell',
          count: c?.notifications ?? undefined,
        },
        row({ key: 'documents', to: ROUTES.documents(), label: 'اسناد', icon: 'doc', count: c?.documents ?? undefined }),
        row({
          key: 'transactions',
          to: ROUTES.walletTransactions(),
          label: 'تاریخچه تراکنش',
          icon: 'wallet',
          count: c?.transactions ?? undefined,
        }),
        row({ key: 'favorites', to: ROUTES.favorites(), label: 'نشان‌شده‌ها', icon: 'star', count: c?.favorites ?? undefined }),
        row({ key: 'requests', to: ROUTES.requests(), label: 'درخواست‌ها', icon: 'flag', count: c?.requests ?? undefined }),
      ],
    },
    inSettings
      ? {
          heading: 'تنظیمات حساب',
          rows: [
            { key: 'settings-profile', to: ROUTES.settingsProfile(), label: profileLabel, icon: 'building' },
            {
              key: 'settings-users',
              to: ROUTES.settingsUsers(),
              label: 'کاربران و نقش‌ها',
              icon: 'users',
              count: settings.data?.subUsers ?? undefined,
            },
            { key: 'settings-kyc', to: ROUTES.settingsKyc(), label: 'احراز هویت', icon: 'shield' },
            { key: 'settings-notifications', to: ROUTES.settingsNotifications(), label: 'تنظیمات اعلان', icon: 'bell' },
            { key: 'settings-security', to: ROUTES.settingsSecurity(), label: 'امنیت و ورود', icon: 'lock' },
          ],
        }
      : {
          rows: [{ key: 'settings', to: ROUTES.settingsProfile(), label: 'تنظیمات', icon: 'settings' }],
        },
  ]

  return <Rail className="account-rail" groups={groups} activeKey={activeOf(pathname)} />
}

/** The layout route: the rail, and beside it whichever account page is open. */
export function AccountArea() {
  return (
    <>
      <AccountRail />
      {/* The rail stays drawn while the next page loads. */}
      <Suspense
        fallback={
          <div className="page">
            <Skeleton h={48} />
            <Skeleton h={240} />
          </div>
        }
      >
        <Outlet />
      </Suspense>
    </>
  )
}
